// src/api/index.js
import axios from 'axios';
import { getCsrfToken } from '../utils';
import API_BASE from './config';

// База берётся из общего конфига. Если пусто, axios будет использовать относительные пути.

const LOGIN_ROUTE = '/login';

// Срок жизни access-token в минутах (должно совпадать с сервером)
const ACCESS_TTL_MINUTES =
  Number((typeof import.meta !== 'undefined' ? import.meta.env?.VITE_ACCESS_TTL_MINUTES : undefined) || 15);

// За сколько минут до конца жизни токена рефрешим заранее
const REFRESH_THRESHOLD_MINUTES =
  Number((typeof import.meta !== 'undefined' ? import.meta.env?.VITE_REFRESH_THRESHOLD_MINUTES : undefined) || 3);

// Heartbeat-проверка (мс)
const PREEMPT_HEARTBEAT_MS = 30_000;

// Ключ для сохранения времени последнего refresh
const PERSIST_KEY = 'access_refreshed_at_ms';

// ===== Локальный "Запомнить меня" (без импорта из utils) =====
const REMEMBER_KEY = 'ses_remember_me';
function getRememberMeLocal() {
  try { return localStorage.getItem(REMEMBER_KEY) === '1'; } catch { return false; }
}

// ===== Вспомогательное: оффлайн/бэкенд-даун =====
const isBackendDownError = (err) =>
  !err || !err.response || err.response?.status === 503; // 503 отдаёт наш setupProxy «тихо»

// axios-инстанс
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 8000, // чтобы в оффлайне быстро отваливалось
});

let isRefreshing = false;
let queue = [];
let didRedirect = false;

const isLoginPath = () =>
  typeof window !== 'undefined' && window.location.pathname === LOGIN_ROUTE;

const redirectToLogin = () => {
  if (didRedirect || isLoginPath()) return;
  didRedirect = true;
  try { localStorage.removeItem(PERSIST_KEY); } catch {}
  window.location.replace(LOGIN_ROUTE);
};

// ─ time math
const ttlMs  = ACCESS_TTL_MINUTES * 60 * 1000;
const thresh = REFRESH_THRESHOLD_MINUTES * 60 * 1000;

// ВАЖНО: не провоцируем мгновенный refresh сразу после старта
let lastRefreshTime = (() => {
  const saved = Number(localStorage.getItem(PERSIST_KEY) || 0);
  if (Number.isFinite(saved) && saved > 0) return saved;
  return Date.now(); // было: "почти истёк" — вызывало /refresh и спам при оффлайне
})();

const subscribe = (resolve, reject) => queue.push({ resolve, reject });
const notifyWaitersSuccess = () => {
  queue.forEach(({ resolve }) => resolve());
  queue = [];
};
const notifyWaitersError = (error) => {
  queue.forEach(({ reject }) => reject(error));
  queue = [];
};

// ─ CSRF
let csrfReady;
const ensureCsrf = async () => {
  if (getCsrfToken()) return;
  if (!csrfReady) {
    csrfReady = axios.get('/csrf-token', {
      baseURL: API_BASE,
      withCredentials: true,
      timeout: 5000,
    });
  }
  await csrfReady;
};

const stampRefreshTime = () => {
  lastRefreshTime = Date.now();
  try { localStorage.setItem(PERSIST_KEY, String(lastRefreshTime)); } catch {}
};

const callRefresh = async () => {
  await ensureCsrf();
  const long = getRememberMeLocal() ? '1' : '0';
  const res = await axios.post(
    '/refresh',
    null,
    {
      baseURL: API_BASE,
      withCredentials: true,
      timeout: 8000,
      headers: {
        'X-CSRF-Token': getCsrfToken(),
        'X-Remember-Me': long,
      },
    },
  );
  stampRefreshTime();
  return res;
};

const isAboutToExpire = () => (Date.now() - lastRefreshTime) >= (ttlMs - thresh);

// ─ request interceptor
api.interceptors.request.use(
  async (config) => {
    const url = config.url || '';

    // Предрефреш перед любым запросом (кроме /login и /refresh), если скоро истечёт
    if (
      !isRefreshing &&
      !url.includes('/login') &&
      !url.includes('/refresh') &&
      !isLoginPath() &&
      isAboutToExpire()
    ) {
      try {
        isRefreshing = true;
        await callRefresh();
        notifyWaitersSuccess();
      } catch (err) {
        notifyWaitersError(err);
        // Если бэкенд недоступен — не редиректим, просто вернём ошибку ниже
        if (isBackendDownError(err)) {
          return Promise.reject(err);
        }
        // Бизнес-кейс: 401 и т.п. — ведём себя как раньше
        redirectToLogin();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    if (isRefreshing) {
      await new Promise((resolve, reject) => subscribe(resolve, reject));
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ─ response interceptor (перестраховка)
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    // Оффлайн/прокси 503 — не редиректим и не пытаемся рефрешить
    if (isBackendDownError(error)) {
      return Promise.reject(error);
    }

    const { response, config } = error || {};
    if (!response || response.status !== 401) return Promise.reject(error);

    // На странице логина не пытаемся рефрешить и не редиректим
    if (isLoginPath()) {
      return Promise.reject(error);
    }

    if (config._retry || config.url?.includes('/login') || config.url?.includes('/refresh')) {
      return Promise.reject(error);
    }
    config._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribe(
          () => api(config).then(resolve).catch(reject),
          (err) => reject(err),
        );
      });
    }

    try {
      isRefreshing = true;
      await callRefresh();
      notifyWaitersSuccess();
      return api(config);
    } catch (err) {
      notifyWaitersError(err);
      redirectToLogin();
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  },
);

// ─ heartbeat (не спамит: триггерится только при «скоро истекает»)
(function startPreemptiveHeartbeat() {
  if (typeof window === 'undefined') return;
  setInterval(async () => {
    try {
      if (document.visibilityState === 'hidden') return;
      if (isLoginPath()) return;
      if (isRefreshing) return;
      if (!isAboutToExpire()) return;

      isRefreshing = true;
      await callRefresh();
      notifyWaitersSuccess();
    } catch (err) {
      notifyWaitersError(err);
      // оффлайн/503 — молча пропускаем; при онлайне всё само восстановится
    } finally {
      isRefreshing = false;
    }
  }, PREEMPT_HEARTBEAT_MS);
})();

export default api;
